import { useEffect, useRef, useState } from 'react';
import { Button, Dialog } from '../../components';
import { useI18n } from '../../lib/i18n';

interface BlurRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Props {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
  onApply: (blurredBlob: Blob) => void | Promise<void>;
}

/**
 * Interactive privacy-blur modal for blurring faces and vehicle number plates
 * on captured evidence photos before sync (Phase 2 privacy tool).
 *
 * Supports:
 * - Dragging a rectangle or tapping on a face / number plate to pixelate & blur it
 * - One-click automatic face detection where browser `FaceDetector` API is available
 * - Undo last region
 */
export function PhotoBlurModal({ open, imageUrl, onClose, onApply }: Props) {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const offscreenRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [rects, setRects] = useState<BlurRect[]>([]);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const hasFaceDetector = typeof window !== 'undefined' && 'FaceDetector' in window;

  useEffect(() => {
    if (!open) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      setRects([]);
      renderCanvas(img, [], null);
    };
    img.src = imageUrl;
  }, [open, imageUrl]);

  useEffect(() => {
    if (imgRef.current) {
      const preview =
        dragStart && dragCurrent
          ? normalizeRect(dragStart.x, dragStart.y, dragCurrent.x, dragCurrent.y)
          : null;
      renderCanvas(imgRef.current, rects, preview);
    }
  }, [rects, dragStart, dragCurrent]);

  function renderCanvas(img: HTMLImageElement, regions: BlurRect[], activePreview: BlurRect | null) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const targetW = img.naturalWidth || img.width || 800;
    const targetH = img.naturalHeight || img.height || 600;
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    for (const r of regions) {
      pixelateRegion(ctx, img, r);
    }

    if (activePreview && activePreview.w > 4 && activePreview.h > 4) {
      ctx.save();
      ctx.strokeStyle = '#e4002b';
      ctx.lineWidth = Math.max(2, Math.round(canvas.width / 300));
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(activePreview.x, activePreview.y, activePreview.w, activePreview.h);
      ctx.restore();
    }
  }

  function pixelateRegion(ctx: CanvasRenderingContext2D, img: HTMLImageElement, r: BlurRect) {
    const x = Math.max(0, Math.round(r.x));
    const y = Math.max(0, Math.round(r.y));
    const w = Math.min(ctx.canvas.width - x, Math.round(r.w));
    const h = Math.min(ctx.canvas.height - y, Math.round(r.h));
    if (w <= 2 || h <= 2) return;

    const sampleW = Math.max(4, Math.round(w / 14));
    const sampleH = Math.max(4, Math.round(h / 14));
    if (!offscreenRef.current) {
      offscreenRef.current = document.createElement('canvas');
    }
    const off = offscreenRef.current;
    if (off.width !== sampleW || off.height !== sampleH) {
      off.width = sampleW;
      off.height = sampleH;
    }
    const octx = off.getContext('2d');
    if (!octx) return;
    octx.clearRect(0, 0, sampleW, sampleH);
    octx.drawImage(img, x, y, w, h, 0, 0, sampleW, sampleH);

    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(off, 0, 0, sampleW, sampleH, x, y, w, h);
    ctx.restore();
  }

  function canvasCoords(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / Math.max(1, rect.width);
    const scaleY = canvas.height / Math.max(1, rect.height);
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    const pt = canvasCoords(e);
    setDragStart(pt);
    setDragCurrent(pt);
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!dragStart) return;
    setDragCurrent(canvasCoords(e));
  }

  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!dragStart) return;
    const end = canvasCoords(e);
    const canvas = canvasRef.current;
    const r = normalizeRect(dragStart.x, dragStart.y, end.x, end.y);
    setDragStart(null);
    setDragCurrent(null);
    if (r.w < 12 && r.h < 12 && canvas) {
      const w = Math.max(48, Math.round(canvas.width * 0.16));
      const h = Math.max(36, Math.round(canvas.height * 0.12));
      const x = Math.max(0, Math.min(canvas.width - w, end.x - w / 2));
      const y = Math.max(0, Math.min(canvas.height - h, end.y - h / 2));
      setRects((prev) => [...prev, { x, y, w, h }]);
    } else if (r.w >= 8 && r.h >= 8) {
      setRects((prev) => [...prev, r]);
    }
  }

  async function autoDetectFaces() {
    if (!hasFaceDetector || !imgRef.current) return;
    try {
      const Detector = (window as unknown as { FaceDetector: new () => { detect: (img: HTMLImageElement) => Promise<{ boundingBox: DOMRectReadOnly }[]> } }).FaceDetector;
      const detector = new Detector();
      const faces = await detector.detect(imgRef.current);
      if (faces.length > 0) {
        setRects((prev) => [
          ...prev,
          ...faces.map((f) => ({
            x: f.boundingBox.x,
            y: f.boundingBox.y,
            w: f.boundingBox.width,
            h: f.boundingBox.height,
          })),
        ]);
      }
    } catch {
      // Ignore if platform face detector fails
    }
  }

  function applyBlur() {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    setSaving(true);
    renderCanvas(imgRef.current, rects, null);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setSaving(false);
          return;
        }
        const file = new File([blob], `blurred-${Date.now()}.jpg`, { type: 'image/jpeg' });
        void Promise.resolve(onApply(file)).finally(() => setSaving(false));
      },
      'image/jpeg',
      0.88,
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('ui.capture.blurPhotoTitle')}
      actions={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={rects.length === 0}
              onClick={() => setRects((prev) => prev.slice(0, -1))}
            >
              {t('ui.capture.blurUndo')}
            </Button>
            {hasFaceDetector && (
              <Button variant="secondary" size="sm" onClick={() => void autoDetectFaces()}>
                {t('ui.capture.blurAutoDetect')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t('ui.common.cancel')}
            </Button>
            <Button variant="primary" loading={saving} disabled={rects.length === 0} onClick={applyBlur}>
              {t('ui.capture.blurApply')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-muted">{t('ui.capture.blurPhotoHint')}</p>
        <div className="relative overflow-hidden rounded-xl border border-border bg-black/90">
          <canvas
            ref={canvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            className="mx-auto block max-h-[55vh] w-full cursor-crosshair touch-none object-contain"
            data-testid="photo-blur-canvas"
          />
        </div>
      </div>
    </Dialog>
  );
}

function normalizeRect(x1: number, y1: number, x2: number, y2: number): BlurRect {
  return {
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    w: Math.abs(x2 - x1),
    h: Math.abs(y2 - y1),
  };
}
